import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useAuth } from '../../context/AuthContext'
import { apiService } from '../../services/api'
import { Icon } from '../shared/ns-icons'

const LOGOUT_ICON = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
)

const COLLAPSE_ICON = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m11 17-5-5 5-5M18 17l-5-5 5-5"/>
  </svg>
)

// ── Nav item definitions (icons match the prototype's shared icon set 1:1) ─────

const ADMIN_ITEMS = [
  { label: 'Dashboard',     route: '/admin',           icon: 'dashboard', exact: true },
  { label: 'Clients',       route: '/admin/clients',   icon: 'users',    badgeKey: 'clients' },
  { label: 'Sites',         route: '/admin/sites',     icon: 'globe',    badgeKey: 'sites' },
  { label: 'Visual Editor', route: '/admin/neweditor', icon: 'edit' },
  { label: 'Media Library', route: '/admin/media',     icon: 'image' },
  { label: 'Requests',      route: '/admin/requests',  icon: 'inbox',    badgeKey: 'requests' },
  { label: 'Analytics',     route: '/admin/analytics', icon: 'chart' },
  { label: 'Edit Logs',     route: '/admin/edit-logs', icon: 'activity' },
  { label: 'Backups',       route: '/admin/backups',   icon: 'archive' },
  { label: 'Domains',       route: '/admin/domains',   icon: 'domain' },
  { label: 'Billing',       route: '/admin/billing',   icon: 'card' },
  { label: 'Team',          route: '/admin/users',     icon: 'user' },
  { label: 'Settings',      route: '/admin/settings',  icon: 'settings' },
]

const CLIENT_ITEMS = [
  { label: 'Home',         route: '/client',           icon: 'dashboard', exact: true },
  { label: 'My sites',     route: '/client/sites',     icon: 'globe' },
  { label: 'Edit my site', route: '/client/editor',    icon: 'edit' },
  { label: 'Requests',     route: '/client/requests',  icon: 'message', badgeKey: 'requests' },
  { label: 'Analytics',    route: '/client/analytics', icon: 'chart' },
  { label: 'Domain',       route: '/client/domains',   icon: 'domain' },
  { label: 'Billing',      route: '/client/billing',   icon: 'card' },
  { label: 'Help',         route: '/client/support',   icon: 'help' },
  { label: 'Settings',     route: '/client/settings',  icon: 'settings' },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function Sidebar({ collapsed, onCollapse, mobileOpen }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const isAdmin = user?.role === 'admin'

  const items = isAdmin ? ADMIN_ITEMS : CLIENT_ITEMS

  // Live counts shown as nav badges. Reuses the ['clients']/['sites'] query keys
  // so it dedupes with the manager screens' caches.
  const { data: clientsData } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: sitesData } = useQuery(
    ['sites'],
    () => apiService.get('/sites').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: requestStats } = useQuery(
    ['request-stats'],
    () => apiService.get('/requests/stats/overview').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: clientPending } = useQuery(
    ['client-pending-requests'],
    () => apiService.get('/requests', { params: { status: 'pending' } })
      .then(r => (Array.isArray(r.data) ? r.data.length : 0)),
    { staleTime: 30000, enabled: !isAdmin, refetchOnWindowFocus: false }
  )

  const counts = isAdmin
    ? {
        clients: clientsData?.clients?.length,
        sites: sitesData?.sites?.length,
        requests: requestStats?.statusBreakdown?.pending,
      }
    : { requests: clientPending }

  function isActive(item) {
    if (item.exact || item.route === '/admin' || item.route === '/client') {
      return location.pathname === item.route || location.pathname === item.route + '/'
    }
    return location.pathname.startsWith(item.route)
  }

  function getInitials(name) {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`} style={{ width: collapsed ? 68 : 248 }}>
      <div className="sidebar-inner">

        {/* Logo / workspace */}
        <div className="ws-switcher">
          <div className="ws-logo">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
              <defs>
                <linearGradient id="wn-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#A78BFA"/>
                  <stop offset="100%" stopColor="#5B7CFF"/>
                </linearGradient>
              </defs>
              <rect width="24" height="24" rx="7" fill="url(#wn-grad)"/>
              <path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ws-name">WebNest</div>
              <div className="ws-meta">{isAdmin ? 'Admin workspace' : 'Client portal'}</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {!collapsed && (
            <div className="nav-section-label">{isAdmin ? 'Workspace' : 'Your site'}</div>
          )}
          {items.map((item) => {
            const active = isActive(item)
            const count = item.badgeKey ? counts[item.badgeKey] : undefined
            const showBadge = typeof count === 'number' && count > 0
            return (
              <button
                key={item.route}
                className={`nav-item${active ? ' active' : ''}`}
                onClick={() => navigate(item.route)}
                title={collapsed ? item.label : undefined}
                style={{ justifyContent: collapsed ? 'center' : undefined }}
              >
                <span className="nav-icon"><Icon name={item.icon} /></span>
                {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                {!collapsed && showBadge && (
                  <span className={`nav-badge${active ? ' on' : ''}`}>{count}</span>
                )}
              </button>
            )
          })}
        </nav>

        <div style={{ flex: 1 }} />

        {/* User + logout at bottom */}
        {!collapsed && (
          <div style={{ padding: '8px 4px', borderTop: '1px solid var(--border)', marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderRadius: 10 }}>
              <div className="ds-avatar ds-avatar-1" style={{ width: 32, height: 32, fontSize: 12 }}>
                {getInitials(user?.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="user-name">{user?.name || 'User'}</div>
                <div className="user-sub">{user?.email}</div>
              </div>
              <button
                className="ds-btn ds-btn-ghost ds-btn-icon"
                onClick={logout}
                title="Sign out"
                style={{ flexShrink: 0 }}
              >
                {LOGOUT_ICON}
              </button>
            </div>
          </div>
        )}
        {collapsed && (
          <button
            className="ds-btn ds-btn-ghost ds-btn-icon"
            onClick={logout}
            title="Sign out"
            style={{ margin: '8px auto' }}
          >
            {LOGOUT_ICON}
          </button>
        )}

        {/* Collapse button */}
        <button
          className="collapse-btn"
          onClick={onCollapse}
          title={collapsed ? 'Expand' : 'Collapse'}
          style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}
        >
          {COLLAPSE_ICON}
        </button>

      </div>
    </aside>
  )
}
